import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { DateTime } from 'luxon';
import React, { useCallback, useState, useMemo } from 'react';

import { accountsSelectors } from 'app-components/flux/accounts';
import { categoriesSelectors } from 'app-components/flux/categories';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { rootUiActions } from 'app-components/flux/root-ui';
import { scheduledTransactionsSelectors, scheduledTransactionsUtils } from 'app-components/flux/scheduled-transactions';
import { transactionsSelectors, transactionsTypes, transactionsUtils } from 'app-components/flux/transactions';
import { tracker } from 'app-components/utils/core';
// DATA
import store from 'app-components/utils/redux-store';
import useTxnActions from 'data/transactionActions/useTxnActions';
import { dispatchSimpleNotification } from 'data/notifications/notificationsUtils';

import LinkIcon from '@mui/icons-material/LinkRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';

import { showMatchTxnDialog } from 'components/Dialogs/MatchTxnDialog/actions';
import type { ProjectSelect } from 'components/ClientProjectSelect';
import { showSplitDialog } from 'components/Transactions/SplitDialog/actions';
import type { SplitProps } from 'app-components/flux/transactions/transactionsTypes';
import type { RecordOf } from 'immutable';

import ConnectedAvatar from 'components/ConnectedAvatar';
import AlertDialog from 'components/Dialogs/AlertDialog';
import StdDialog from 'components/Dialogs/StdDialog';
import StdDialogTitle from 'components/Dialogs/StdDialogTitle';

import { dateSelectors } from 'app-components/flux/date';
import showConfirmationDialog from 'components/Dialogs/ConfirmationDialog/show';
import {
  getRootModalRemoveDialogOptions,
  type RootModalDrawerOptions,
} from 'components/Drawers/standardDrawerContract';
import QAmountField from 'components/QAmountField';
// CUSTOM
import QButton from 'components/QButton';
import AccountField from 'components/QuickenControls/AccountField';
import CategoryField from 'components/QuickenControls/CategoryField';
import SPayeeField from 'components/SPayeeField';
import DateField from 'components/Transactions/DateField';
import VisibilitySection from 'components/Transactions/VisibilitySection';
import {
  getExcludedFromReportsDisplayState,
  getExcludedFromSpendingPlanDisplayState,
} from 'components/Transactions/VisibilitySection/utils';

import {
  closeScheduledTransactionEditDialog,
  showConfirmationIfDirty,
  showScheduledTransactionEditDialog,
} from 'components/Dialogs/ScheduledTransactionEditDialog';
import useQDialogs from 'components/QDialogs/useQDialogs';
import StdDialogContent from '../StdDialogContent';
import StdDialogActions from '../StdDialogActions';
import BillStatus from './BillStatus';
import { chartOfAccountsSelectors } from 'app-components/flux/chart-of-accounts';
import TransactionSplits from 'components/TransactionSplits';
import { doesSplitModeAllowPaymentOrnaments, splitItemsToArray } from 'app-components/flux/transactions/splitsHelpers';

const useStyles = makeStyles()((theme) => ({
  visibilitySection: {
    display: 'flex',
    flexDirection: 'column',
    '> p': {
      padding: 0,
      margin: 0,
    },
    padding: theme.spacing(0, 2, 3, 2),
    gap: theme.spacing(1),
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    padding: theme.spacing(2, 3),
    '& > .MuiBox-root': {
      width: `calc(100% + ${theme.spacing(15)})`,
      paddingRight: theme.spacing(13.25),
      '> div': {
        gap: theme.spacing(3),
      },
    },
  },
  contentInner: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
  },
  avatarContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(2),
    width: '100%',
    minWidth: 0,
  },
  row: {
    display: 'flex',
  },
  payee: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    paddingBottom: 8,
  },
  amtTxt: {
    width: '68%',
    textAlign: 'left',
    paddingRight: 16,
    '> div': {
      margin: 0,
    },
  },
  dateField: {
    margin: 0,
  },
  manualFields: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    paddingRight: 80,
    paddingBottom: 12,
  },
  linkRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  linkTxnButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    color: theme.palette.primary.main,
    fontWeight: 500,
  },
  linkIcon: {
    height: 18,
    width: 18,
  },
  divider: {
    width: '100%',
    height: 1,
    backgroundColor: theme.palette.divider,
  },
}));

interface EditReminderDialogProps {
  txn: transactionsTypes.TransactionRecord;
  context?: string;
  dialogId?: string;
  rootModalDrawer?: RootModalDrawerOptions;
}

// ### - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - ###
// ### - - -                  Dialog Component                   - - - ###
// ### - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - ###
function EditReminderDialog(props: EditReminderDialogProps) {
  const { classes } = useStyles();
  const theme = useTheme();
  const { dialogAlert } = useQDialogs();
  const { txn, context = '', dialogId, rootModalDrawer } = props;
  const initialAmount = txn?.amount?.toFixed(2) || '0.00';

  const { saveTransactions, performTransactionAction } = useTxnActions({ wrapperId: context });

  const dispatch = useAppDispatch();
  const source =
    window.location.pathname === '/'
      ? 'dashboard'
      : window.location.pathname.replaceAll?.(/[/-]|(\d+)/g, (match) =>
          match === '/' ? '' : match === '-' ? ' ' : 'xxx',
        );

  const isPendingScheduledTxn = txn.source === 'SCHEDULED_TRANSACTION_PENDING';
  const hideDismissReminderAction = transactionsUtils.isPlannedA2ATransfer(txn);
  const futureTxnsFf = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'futureTxns'));
  const splitSeriesFf = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'splitSeries'));

  // +++     State values     +++
  const [amount, setAmount] = useState(initialAmount);
  const [date, setDate] = useState(DateTime.fromISO(txn.postedOn));
  const [dateError, setDateError] = useState('');
  const [excludeReports, setExcludeReports] = useState(Boolean(txn.isExcludedFromReports));
  const [excludeF2S, setExcludeF2S] = useState(Boolean(txn.isExcludedFromF2S));
  const [excludeF2SOverridden, setExcludeF2SOverridden] = useState(false);

  const [edit, setEdit] = useState(false);
  const [dirtyDialog, setDirtyDialog] = useState(false);
  const [payee, setPayee] = useState(txn.payee);
  const [coa, setCoa] = useState(txn.coa);
  const [accountId, setAccount] = useState(txn.accountId);

  const splitsFeatureFlag = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'splitSeries'));
  const splitsDisabled = !splitsFeatureFlag;

  const model = useAppSelector((state) =>
    scheduledTransactionsSelectors.getScheduledTransactionById(state, txn?.stModelId || ''),
  );

  const txnFromStore = useAppSelector((state) =>
    txn?.id ? transactionsSelectors.getTransactionById(state, txn.id) || txn : txn,
  );
  const transactionsById = useAppSelector(transactionsSelectors.getResourcesById);
  // Draft split state for Split Reminder flow:
  // - undefined: no edits yet (use persisted txn split)
  // - null: user explicitly cleared splits
  // - Record: user edited/created splits
  const [splitDraft, setSplitDraft] = useState<RecordOf<SplitProps> | null | undefined>(undefined);

  const effectiveSplit: SplitProps | null | undefined = useMemo(() => {
    if (splitDraft !== undefined) {
      return splitDraft ? (splitDraft.toJS() as SplitProps) : null;
    }
    return txnFromStore.toJS().split ?? null;
  }, [splitDraft, txnFromStore]);

  const txnPropsForSplits = useMemo(
    () =>
      ({
        ...(txnFromStore.toJS() as transactionsTypes.TransactionProps),
        split: effectiveSplit ?? null,
      }) as transactionsTypes.TransactionProps,
    [effectiveSplit, txnFromStore],
  );

  const excludeF2SDisplayFromTxn = useMemo(
    () =>
      getExcludedFromSpendingPlanDisplayState(
        {
          isExcludedFromF2S: excludeF2S,
          split: effectiveSplit ?? undefined,
        },
        transactionsById,
      ),
    [effectiveSplit, excludeF2S, transactionsById],
  );

  const excludeF2SDisplay = useMemo(() => {
    if (excludeF2SOverridden) {
      return { value: excludeF2S, indeterminate: false };
    }
    return excludeF2SDisplayFromTxn;
  }, [excludeF2S, excludeF2SDisplayFromTxn, excludeF2SOverridden]);

  const excludeReportsDisplayFromTxn = useMemo(
    () =>
      getExcludedFromReportsDisplayState(
        {
          isExcludedFromReports: excludeReports,
          split: effectiveSplit ?? undefined,
        },
        transactionsById,
      ),
    [effectiveSplit, excludeReports, transactionsById],
  );

  const hasSplitsAssigned =
    transactionsUtils.isSplitTxn(txnPropsForSplits) || transactionsUtils.isSplitTxn(txnFromStore);
  const hasTxnSplitItems = Boolean(
    (effectiveSplit?.items && splitItemsToArray(effectiveSplit.items).length > 0) || false,
  );
  const visibilityF2SDisplay = useMemo(
    () => (hasTxnSplitItems ? excludeF2SDisplayFromTxn : excludeF2SDisplay),
    [excludeF2SDisplay, excludeF2SDisplayFromTxn, hasTxnSplitItems],
  );
  const visibilityExcludeReportsDisplay = useMemo(
    () => (hasTxnSplitItems ? excludeReportsDisplayFromTxn : { value: excludeReports, indeterminate: false as const }),
    [excludeReports, excludeReportsDisplayFromTxn, hasTxnSplitItems],
  );
  const modelSplitItems = model?.transaction?.split?.items;
  const hasModelSplitItems = Boolean(modelSplitItems && splitItemsToArray(modelSplitItems).length > 0);
  const hideSplitsLineForUnmaterializedReminder = Boolean(!hasTxnSplitItems && hasModelSplitItems);
  const [_customersAndProjects, setCustomersAndProjects] = useState<ProjectSelect>({
    projectId: '',
    customerId: '',
    billable: false,
  });
  const pendingSplitRef = React.useRef<RecordOf<SplitProps> | null>(null);
  const pendingAmountRef = React.useRef<number | null>(null);
  const pendingCoaRef = React.useRef<transactionsTypes.TransactionProps['coa'] | null | undefined>(undefined);
  const pendingTagsRef = React.useRef<transactionsTypes.TransactionProps['tags'] | undefined>(undefined);

  const currentDate = useAppSelector(dateSelectors.getCurrentDate);
  const yearFromCurrentDate = currentDate.plus({ year: 1 });

  const scheduledTxnType = model && scheduledTransactionsUtils.typeFromScheduledTransaction(model)?.toLowerCase();

  const accounts = useAppSelector(accountsSelectors.getAccountsById);
  const accountName = accounts?.get(txn?.accountId || '')?.name;
  const isDirty =
    amount !== initialAmount ||
    date.toISODate() !== txn.postedOn ||
    excludeReports !== Boolean(txn.isExcludedFromReports) ||
    excludeF2S !== Boolean(txn.isExcludedFromF2S) ||
    txn.coa !== coa ||
    txn.accountId !== accountId ||
    txn.payee !== payee ||
    splitDraft !== undefined;

  const isIncome = useAppSelector((state) => {
    const incomeCOAIds = categoriesSelectors.getIncomeCOAIds(state);
    if (model?.transaction) {
      return scheduledTransactionsUtils.isIncomeSeriesFromTxn(model.transaction, incomeCOAIds);
    }
    return transactionsSelectors.isIncomeTxn(state, txn);
  });
  //  TODO: Move disable to shared component in app-components DISABLEREPORTS
  const disableReports = coa?.type === 'BALANCE_ADJUSTMENT';

  const avatarColor = useMemo(() => {
    const amountNumber = Number(amount);
    return !Number.isNaN(amountNumber) && amountNumber > 0 ? theme.palette.legend.income : undefined;
  }, [amount, theme.palette.legend.income]);

  const clearDialog = useCallback(
    (animate = Boolean(rootModalDrawer)) => {
      dispatch(rootUiActions.removeDialog(dialogId, animate ? { animate: true } : undefined));
    },
    [dialogId, dispatch, rootModalDrawer],
  );

  const onSave = useCallback(() => {
    if (dateError) {
      return;
    }

    const splitItems = effectiveSplit?.items;
    const hasSplitItems = splitItems ? splitItemsToArray(splitItems).length > 0 : false;

    const txnToSave = transactionsTypes.mkCashFlowTransaction({
      ...txnFromStore.toJS(),
      isExcludedFromF2S: excludeF2S,
      isExcludedFromReports: excludeReports,
      postedOn: date.toISODate() || txn.postedOn,
      amount: Number(Number(amount).toFixed(2)),
      // Mirror mobile/web behavior: when splits exist, parent txn should not carry coa/tags or business fields.
      tags: hasSplitItems ? [] : txn.tags,
      coa: hasSplitItems ? null : coa,
      customerId: hasSplitItems ? null : txn.customerId,
      projectId: hasSplitItems ? null : txn.projectId,
      isBillable: hasSplitItems ? false : txn.isBillable,
      split: effectiveSplit ?? null,
      accountId,
      payee,
    } as Partial<transactionsTypes.TransactionProps>);

    saveTransactions([txnToSave]);

    tracker.track(tracker.events.changeReminder, {
      type: scheduledTxnType || 'other',
      source: source,
      excludeFromSpendingPlan: excludeF2S,
      excludeFromReports: excludeReports,
      payee: payee,
      category: coa?.id ? chartOfAccountsSelectors.getCoaStringSelector(null, coa, true) : 'uncategorized',
    });

    clearDialog();
  }, [
    dateError,
    txn,
    txnFromStore,
    excludeF2S,
    excludeReports,
    date,
    amount,
    coa,
    accountId,
    payee,
    effectiveSplit,
    saveTransactions,
    clearDialog,
    scheduledTxnType,
    source,
  ]);

  const openEdit = () => {
    clearDialog(false);
    return (
      model &&
      dispatch(
        showScheduledTransactionEditDialog({
          open: true,
          rootModalDrawer,
          scheduledTransaction: model,
          onClose: showConfirmationIfDirty(dialogAlert, () => {
            dispatch(closeScheduledTransactionEditDialog());
          }),
        }),
      )
    );
  };

  const openReminderSplits = useCallback(() => {
    const txnProps = txnFromStore.toJS() as transactionsTypes.TransactionProps;
    // Use the draft-backed split in the Split Reminder dialog so internal logic (e.g. disabled states)
    // and any callers inspecting txn.split see the draft state, not the persisted state.
    const txnPropsForSplitDialog: transactionsTypes.TransactionProps = {
      ...txnProps,
      split: effectiveSplit ?? null,
    };
    const dialogId = `SPLIT_DIALOG_REMINDER_${txnProps?.id || txnProps?.clientId || Date.now()}`;

    const splitRecord =
      splitDraft !== undefined
        ? splitDraft
        : txnProps.split
          ? (transactionsTypes.mkSplit(txnProps.split as SplitProps) as RecordOf<SplitProps>)
          : null;

    pendingSplitRef.current = splitRecord;
    pendingAmountRef.current = txnProps.amount ?? null;
    pendingCoaRef.current = txnProps.coa ?? null;
    pendingTagsRef.current = txnProps.tags;
    setCustomersAndProjects({ projectId: '', customerId: '', billable: false });

    const closeSplitDialog = () =>
      dispatch(rootUiActions.removeDialog(dialogId, getRootModalRemoveDialogOptions(rootModalDrawer)));

    dispatch(
      showSplitDialog({
        id: dialogId,
        mode: 'reminder',
        txn: txnPropsForSplitDialog,
        setSplit: (val) => {
          pendingSplitRef.current = val;
          // Important: `null` means the user explicitly cleared splits (e.g., "Delete All Splits").
          setSplitDraft(val === null ? null : val);
        },
        setAmount: (val) => {
          pendingAmountRef.current = val;
          setAmount(Number(val).toFixed(2));
        },
        setCoa: (newCOA) => {
          pendingCoaRef.current = newCOA ?? null;
          setCoa(newCOA ?? null);
        },
        setTags: (val) => {
          pendingTagsRef.current = val;
        },
        accountId: txnProps.accountId ?? '',
        disabledPaymentFn: () => !doesSplitModeAllowPaymentOrnaments('reminder'),
        setCustomersAndProjects,
        rootModalDrawer,
        onBack: () => {
          closeSplitDialog();
        },
        onClose: closeSplitDialog,
      }),
    );
  }, [dispatch, effectiveSplit, rootModalDrawer, splitDraft, txnFromStore]);

  const stopEvent = (e: React.SyntheticEvent): void => {
    e.preventDefault();
    e.stopPropagation();
  };

  const stopEventAndOpenReminderSplits = (e: React.SyntheticEvent): void => {
    stopEvent(e);
    openReminderSplits();
  };

  const handleReminderAmountKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter') {
      stopEventAndOpenReminderSplits(e);
      return;
    }
    stopEvent(e);
  };

  const closeCheck = (e?: unknown, reason?: string) => {
    if (isDirty && !dateError) {
      if (reason === 'ENTER_KEY_DOWN') {
        onSave();
      } else {
        setDirtyDialog(true);
      }
    }
  };

  const closeAndLink = () => {
    closeCheck();
    if (!rootModalDrawer) {
      clearDialog(false);
    }
    dispatch(
      showMatchTxnDialog({
        txn,
        rootModalDrawer,
        wrapperId: context,
        onLinked: () => {
          clearDialog();
          dispatchSimpleNotification('Transaction linked', {
            severity: 'success',
            anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
            autoHideDuration: 3000,
          });
        },
      }),
    );
  };

  const amountChange = (e: React.ChangeEvent<HTMLInputElement>) => setAmount(e.target.value);

  // TODO: replace 'skip' with 'delete' after backend supports deleting single reminder in series
  const onSkip = () => {
    if (store) {
      const prevReminders = transactionsSelectors.getPreviousUnlinkedReminders(store.getState(), {
        stModelId: txn.stModelId,
        postedOn: txn.postedOn,
      });
      showConfirmationDialog({
        size: 'sm',
        title: 'Dismiss reminder',
        content: `Are you sure you want to dismiss this reminder? \n\n${
          prevReminders?.length ? 'This will also dismiss all earlier reminders in the series.' : ''
        } Future reminders won’t be affected.`,
        confirmLabel: 'Dismiss Reminder',
        confirmAction: () => {
          performTransactionAction({
            action: 'skip',
            id: txn.id,
          });
          tracker.track(tracker.events.deleteReminder, {
            context,
            prevReminders: prevReminders?.length || 0,
            source: 'Edit Reminder Dialog',
          });
          clearDialog();
        },
      });
    }
  };

  const onHandleClose = useCallback(
    (e?: any, reason?: string) => {
      if (reason === 'SUBMIT_BUTTON' || reason === 'ENTER_KEY_DOWN') {
        // update
        onSave();
      }
      clearDialog();
    },
    [clearDialog, onSave],
  );

  const title = isPendingScheduledTxn ? 'Edit Reminder' : 'Edit Transaction';
  const content = (
    <>
      <StdDialogContent
        id='edit-reminder-dialog-content'
        className={classes.content}
        sx={rootModalDrawer ? { flex: '1 1 auto', minHeight: 0 } : undefined}
      >
        <div id='edit-reminder-dialog-content-inner' className={classes.contentInner}>
          <div className={classes.avatarContainer}>
            <ConnectedAvatar
              seriesIcon
              isCurrentCategoryIncome={isIncome}
              model={model}
              txn={txn}
              size={64}
              iconSize={24}
              color={avatarColor}
              editable
            />
            {isPendingScheduledTxn ? (
              <div className={classes.payee}>
                <Typography variant='displayRegular' noWrap>
                  {txn.payee}
                </Typography>
                <Typography variant='bodyRegular'>{accountName}</Typography>
              </div>
            ) : (
              <SPayeeField
                fullWidth
                defaultValue={payee}
                onChange={setPayee}
                id='edit-reminder-payee-field'
                autoFocus
              />
            )}
          </div>

          <div className={classes.row}>
            <QAmountField
              value={amount}
              onChange={amountChange}
              expectedSign={isIncome ? 'positive' : 'negative'}
              showColor='positive'
              fullWidth
              editable
              label='Amount'
              id='Reminder_Amount'
              margin='normal'
              className={classes.amtTxt}
              InputProps={
                splitSeriesFf && hasSplitsAssigned
                  ? {
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position='end'>
                          <ChevronRightRoundedIcon color='action' />
                        </InputAdornment>
                      ),
                    }
                  : undefined
              }
              slotProps={
                splitSeriesFf && hasSplitsAssigned
                  ? {
                      htmlInput: {
                        readOnly: true,
                        'aria-readonly': true,
                      },
                    }
                  : undefined
              }
              textFieldProps={
                splitSeriesFf && hasSplitsAssigned
                  ? {
                      onKeyDown: handleReminderAmountKeyDown,
                      onMouseDown: stopEvent,
                      onClick: stopEventAndOpenReminderSplits,
                    }
                  : undefined
              }
              sx={
                splitSeriesFf && hasSplitsAssigned
                  ? {
                      cursor: 'pointer',
                      '& .MuiInputBase-root': {
                        cursor: 'pointer',
                        backgroundColor: (theme) => theme.palette.background.default,
                      },
                      '& input': { cursor: 'pointer' },
                    }
                  : undefined
              }
            />

            {/* Date Field */}
            <DateField
              className={classes.dateField}
              maxDate={futureTxnsFf ? yearFromCurrentDate : undefined}
              date={date}
              onChange={(date: DateTime) => {
                if (futureTxnsFf && date > yearFromCurrentDate) {
                  setDateError(`Date must be before ${yearFromCurrentDate.toFormat('MM/dd/yyyy')}`);
                } else {
                  setDateError('');
                }

                setDate(date);
              }}
              error={!!dateError}
              helperText={dateError}
            />
          </div>

          {/* Bill Status */}
          <BillStatus txn={txn} model={model} onClose={clearDialog} rootModalDrawer={rootModalDrawer} />

          {/* manual transaction fields */}
          {!isPendingScheduledTxn && (
            <div className={classes.manualFields}>
              <AccountField
                initialAccountId={accountId}
                onSelected={(selectedAccount?: any) => setAccount(selectedAccount?.id)}
                label='Account'
                variant='filled'
                margin='normal'
                fullWidth
              />

              {!hasSplitsAssigned && (
                <CategoryField
                  variant='bodyRegular'
                  id={txn.id}
                  editable
                  value={coa}
                  label='Category'
                  onChange={(txnCoa: any) => setCoa(txnCoa)}
                  textFieldVariant='filled'
                  margin='normal'
                />
              )}
            </div>
          )}

          {/* link option */}
          {isPendingScheduledTxn && (
            <Typography variant='paragraphRegular' className={classes.linkRow}>
              Already {isIncome ? 'received' : 'paid'}?
              {splitSeriesFf ? (
                <Link
                  component='button'
                  type='button'
                  id='link-button'
                  aria-label='link-transaction'
                  underline='none'
                  onClick={closeAndLink}
                  className={classes.linkTxnButton}
                >
                  <LinkIcon className={classes.linkIcon} />
                  Link transaction
                </Link>
              ) : (
                <QButton type='button' id='link-button' aria-label='link-transaction' onClick={closeAndLink}>
                  <LinkIcon style={{ marginRight: 4 }} />
                  <Typography variant='bodyMedium'>Link transaction</Typography>
                </QButton>
              )}
            </Typography>
          )}

          {/* Series splits summary / Assign splits CTA */}
          {model && !hideSplitsLineForUnmaterializedReminder && (
            <TransactionSplits
              transaction={txnPropsForSplits}
              onAssignSplits={openReminderSplits}
              variant='series'
              disabledSplitsEdit={splitsDisabled}
              includeOverflowSplitLine
            />
          )}

          <div className={classes.divider} />

          {/* Ignore Checkboxes */}
          <VisibilitySection
            className={classes.visibilitySection}
            disabledReports={disableReports}
            excludeF2S={visibilityF2SDisplay.indeterminate ? null : visibilityF2SDisplay.value}
            indeterminateF2S={visibilityF2SDisplay.indeterminate}
            setExcludeF2S={(val) => {
              setExcludeF2SOverridden(true);
              setExcludeF2S(val);
            }}
            excludeReports={
              disableReports
                ? true
                : visibilityExcludeReportsDisplay.indeterminate
                  ? null
                  : visibilityExcludeReportsDisplay.value
            }
            setExcludeReports={setExcludeReports}
            indeterminateReports={visibilityExcludeReportsDisplay.indeterminate}
            managedPerSplit={hasTxnSplitItems}
            onEditSplits={hasTxnSplitItems ? openReminderSplits : undefined}
            editSplitsDisabled={hasTxnSplitItems ? splitsDisabled : undefined}
          />
        </div>
      </StdDialogContent>
      <StdDialogActions
        divider={Boolean(rootModalDrawer)}
        tertiaryAction={
          isPendingScheduledTxn && model && !hideDismissReminderAction
            ? {
                id: 'delete-reminder',
                children: 'Dismiss reminder',
                onClick: onSkip,
                variant: 'text',
                color: 'error',
                disabled: !txn || !txn.id,
              }
            : undefined
        }
        secondary={
          isPendingScheduledTxn && model
            ? {
                id: 'edit-series',
                children: 'Edit Series',
                onClick: openEdit,
                variant: 'outlined',
              }
            : hideDismissReminderAction
              ? undefined
              : {
                  id: 'delete-reminder',
                  children: 'Dismiss reminder',
                  onClick: onSkip,
                  variant: 'text',
                  color: 'error',
                  disabled: !txn || !txn.id,
                }
        }
        primary={{
          id: 'create-update-recurrence',
          children: 'Update',
          onClick: onSave,
          disabled: !isDirty || !!dateError,
        }}
      />
    </>
  );

  return (
    <>
      {rootModalDrawer ? (
        <Box
          aria-label={title}
          role='dialog'
          sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}
        >
          <StdDialogTitle variant='drawer' id='dialog-title' onClose={onHandleClose} showCloseButton>
            {title}
          </StdDialogTitle>
          {content}
        </Box>
      ) : (
        <StdDialog
          onClose={onHandleClose}
          open={!edit}
          sharedcomponentid='EDIT_REMINDER_DIALOG'
          size='sm'
          title={title}
        >
          {content}
        </StdDialog>
      )}

      {dirtyDialog && (
        <AlertDialog
          dialogId='ignore this warning'
          title='Just checking...'
          content='Are you sure you want to discard all changes?'
          onClose={(btnObj: { btnPressed: string }) => {
            setDirtyDialog(false);
            if (btnObj.btnPressed === 'Discard') {
              if (edit) {
                setEdit(false);
              }
            }
          }}
          buttons={['Go Back', 'Discard']}
        />
      )}
    </>
  );
}

EditReminderDialog.displayName = 'EditReminderDialog';

export default EditReminderDialog;
